import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AwaitContainer } from '@ibsys/kit';
import { PageContainer } from '@/shared/ui';
import { StrategistFilters } from '@/widgets/StrategistFilters';
import type { StrategistFiltersValue } from '@/widgets/StrategistFilters';
import { defaultThreshold, isThresholdApplied, useStrategistMatrix } from '@/entities/strategist';
import type { StrategistMatrixRequest, StrategistTargetAttribute } from '@/entities/strategist';
import { ConceptCardDrawer, useOpenConceptCard } from '@/features/concept-card';
import { DialogsDrawerContent, useOpenDialogsDrawer } from '@/features/dialogs-drawer';
import { StrategistMatrixControls } from './StrategistMatrixControls';
import { StrategistMatrixViz } from './StrategistMatrixViz';

/** Число строк/столбцов матрицы по умолчанию. */
const DEFAULT_TOP = 15;

/**
 * Страница «Матрица возражение × аргумент» раздела «Стратег».
 * Держит состояние сквозного фильтра, целевой метрики и размера матрицы,
 * запрашивает данные (`useStrategistMatrix`) и рендерит визуализацию.
 */
export const StrategistMatrixPage: FC = () => {
  const { t } = useTranslation(['common', 'strategist']);
  const [filterValue, setFilterValue] = useState<StrategistFiltersValue>({ filters: [] });
  const [target, setTarget] = useState<StrategistTargetAttribute>({ retention: true });
  const [top, setTop] = useState<number>(DEFAULT_TOP);
  // Пороги близости (по возражениям / по аргументам); инициализируются из первого ответа.
  const [objectionThreshold, setObjectionThreshold] = useState<number | undefined>(undefined);
  const [argumentThreshold, setArgumentThreshold] = useState<number | undefined>(undefined);

  const body = useMemo<StrategistMatrixRequest>(
    () => ({
      filters: filterValue.filters,
      dateFrom: filterValue.dateFrom,
      dateTo: filterValue.dateTo,
      targetAttribute: target,
      top,
      objectionThreshold: objectionThreshold,
      argumentThreshold: argumentThreshold,
    }),
    [filterValue, target, top, objectionThreshold, argumentThreshold],
  );

  const { data, isLoading, isPlaceholderData } = useStrategistMatrix(body);

  // Показывать можно только данные, посчитанные теми же параметрами, что уйдут в срез ячейки:
  // пока идёт запрос за новыми или пороги ещё не подставлены, матрица и панель диалогов разойдутся.
  const isAwaitingData =
    isLoading || isPlaceholderData || !isThresholdApplied(data?.thresholds, [objectionThreshold, argumentThreshold]);

  // Пороги берутся из первого ответа: доступные значения известны только по нему.
  useEffect(() => {
    if (!data) return;
    setObjectionThreshold((prev) => prev ?? defaultThreshold(data.thresholds));
    setArgumentThreshold((prev) => prev ?? defaultThreshold(data.thresholds));
  }, [data]);

  const handleConceptOpen = useOpenConceptCard(filterValue);

  const openDialogs = useOpenDialogsDrawer({ ...filterValue, target });
  const handleCellOpen = useCallback(
    (params: { objectionId: string; argumentId: string; title: string }) => {
      openDialogs({ ...params, objectionThreshold, argumentThreshold });
    },
    [openDialogs, objectionThreshold, argumentThreshold],
  );

  return (
    <PageContainer className="flex flex-col gap-5">
      <h1 className="text-xl font-semibold text-foreground">{t('common:routes.strategistMatrix')}</h1>
      <StrategistFilters value={filterValue} onChange={setFilterValue} />
      <StrategistMatrixControls
        target={target}
        onTargetChange={setTarget}
        top={top}
        onTopChange={setTop}
        thresholds={data?.thresholds ?? []}
        objectionThreshold={objectionThreshold}
        onObjectionThresholdChange={setObjectionThreshold}
        argumentThreshold={argumentThreshold}
        onArgumentThresholdChange={setArgumentThreshold}
      />
      <div className="flex-1 min-h-0">
        <AwaitContainer isLoading={isAwaitingData}>
          {!data || data.rows.length === 0 || data.columns.length === 0 ? (
            <div className="flex h-40 items-center justify-center rounded-xl border border-border bg-card text-sm text-muted-foreground">
              {t('common:emptyTable')}
            </div>
          ) : (
            <StrategistMatrixViz data={data} onConceptOpen={handleConceptOpen} onCellOpen={handleCellOpen} />
          )}
        </AwaitContainer>
      </div>
      <ConceptCardDrawer />
      <DialogsDrawerContent />
    </PageContainer>
  );
};
